// src/services/logoutService.js
import { getApiBaseURL } from "../api/apiBaseUrl";
import { clearNotificationStorageForLogout } from "../notifications/storageKeys";
import { unregisterLocalWebPush } from "../notifications/webPushClient";
import { purgeWorkspaceBrowserStorageOnLogout, getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import {
  readTabBearerToken,
  readTabUserData,
  clearTabAuthStorage,
} from "../utils/tabAuthStorage";

let apiBaseURLOverride = "";
let uiHandler = null;
let isRunning = false;
/** Contador: enquanto > 0, 401/refresh falho não deve derrubar a sessão (ex.: Migrar todos). */
let unauthorizedLogoutSuppressDepth = 0;

export const setApiBaseURL = (url) => {
  apiBaseURLOverride = url || "";
};

export const registerLogoutHandler = (fn) => {
  uiHandler = typeof fn === "function" ? fn : null;
};

/** Operações longas (ex. auto-migrate) — evita logout por timeout/401 de polling em paralelo. */
export const beginSuppressUnauthorizedLogout = () => {
  unauthorizedLogoutSuppressDepth += 1;
};

export const endSuppressUnauthorizedLogout = () => {
  unauthorizedLogoutSuppressDepth = Math.max(0, unauthorizedLogoutSuppressDepth - 1);
};

export const isUnauthorizedLogoutSuppressed = () => unauthorizedLogoutSuppressDepth > 0;

/** Mesmo critério do interceptor axios (`client.js`) para o header Company. */
function resolveLogoutCompanyHeader(scope) {
  if (scope === "platform" || scope === "partner") {
    return "xprobase";
  }
  try {
    const tabSlug = getActiveWorkspaceSlug();
    if (tabSlug) return tabSlug;
    const raw = localStorage.getItem("company");
    const s = raw != null ? String(raw).trim() : "";
    if (!s) return null;
    if (/^\d+$/.test(s)) {
      const fb = localStorage.getItem("xproWorkspaceDatabase");
      return fb != null && String(fb).trim() ? String(fb).trim() : null;
    }
    return s;
  } catch {
    return null;
  }
}

/**
 * Envia logout ao servidor sem depender do axios/interceptors (evita loop).
 * Grava data_logout no tenant (compatível com regras de automação do Xpro legado).
 */
const serverBeaconLogout = async (reason) => {
  try {
    const token = readTabBearerToken();
    const baseURL = apiBaseURLOverride || getApiBaseURL();
    if (!token || !baseURL) return;

    let scope = "workspace";
    try {
      const u = readTabUserData();
      if (u?.scope) scope = u.scope;
    } catch {
      /* ignore */
    }

    const company = resolveLogoutCompanyHeader(scope);
    if (!company) return;

    await fetch(`${baseURL}/logout`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Company: company,
        "X-Xpro-Client": "browser",
      },
      body: JSON.stringify({ reason }),
    });
  } catch {
    /* silencioso */
  }
};

/**
 * Limpeza de storage 100% centralizada — só desta aba (auth em sessionStorage).
 */
const clearStorages = () => {
  try {
    let scope = "workspace";
    try {
      const u = readTabUserData();
      if (u && typeof u.scope === "string") scope = u.scope;
    } catch {
      /* ignore */
    }
    if (scope === "platform" || scope === "partner") {
      localStorage.removeItem("company");
      localStorage.removeItem("xproWorkspaceDatabase");
    }

    localStorage.removeItem("currentPage");
    clearTabAuthStorage();
    purgeWorkspaceBrowserStorageOnLogout();

    sessionStorage.setItem("firstLoad", true);

    clearNotificationStorageForLogout();
  } catch {
    /* silencioso */
  }
};

/**
 * Logout unificado e idempotente.
 * Afeta apenas a aba atual — outras abas com outros usuários permanecem logadas.
 * @param {string} reason - ex: "manual" | "unauthorized" | "refresh_failed" | "inactivity"
 * @param {{redirect?: boolean}} opts
 */
export const unifiedLogout = async (reason = "manual", opts = { redirect: true }) => {
  if (isRunning) return;     // idempotente: evita múltiplas execuções simultâneas
  // Manual sempre passa; 401/refresh durante Migrar todos (e similares) fica bloqueado.
  if (reason !== "manual" && unauthorizedLogoutSuppressDepth > 0) {
    console.warn("Logout adiado (operação longa em andamento):", reason);
    return;
  }
  isRunning = true;

  try {
    await serverBeaconLogout(reason);
    await unregisterLocalWebPush();
  } finally {
    let redirectTarget = "/login";
    try {
      const u = readTabUserData();
      const scope = u?.scope;
      if (scope === "partner") redirectTarget = "/partner/login";
      if (scope === "platform") redirectTarget = "/platform/login";
    } catch {
      /* ignore */
    }
    clearStorages();
    try {
      if (uiHandler) await uiHandler(); // limpa estados React (setters)
    } catch {
      /* silencioso */
    }
    if (opts?.redirect) {
      window.location.href = redirectTarget;
    }
    isRunning = false;
  }
};
